import React from 'react';
import SVGInline from 'components/SVGInline/SVGInline';
import icons from 'style';
import translate from 'utils/fnTranslate';
import { AriaHint, Option, OptionButton, OptionIcon, OptionName, OptionNameSub, OptionRightSide } from './PageSettingsCommon.css';

interface OptionFocusableProps {
    title: string;
    subtitle?: string;
    icon?: string;
    actionComponent?: any;
    isExternalLink?: boolean;
    dataTestId?: string;
    isDisabled?: boolean;
    ariaHint?: string;
    role?: string;
    'aria-checked'?: boolean;
    onClick?: () => void;
}

const OptionFocusable: React.FC<OptionFocusableProps> = ({
    title,
    subtitle,
    icon,
    actionComponent,
    isExternalLink,
    dataTestId,
    isDisabled,
    ariaHint,
    role: roleOverride,
    'aria-checked': ariaChecked,
    onClick,
}) => {
    const isInteractive = !!onClick;
    const role = roleOverride ?? (isInteractive ? 'link' : undefined);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (isDisabled || !onClick) return;

        if (e.key === 'Enter' || (e.key === ' ' && role !== 'link')) {
            e.preventDefault();
            onClick();
        }
    };

    return (
        <Option
            data-test-id={`header-account-settingsbuttons-${dataTestId}`}
            tabIndex={!isInteractive ? undefined : isDisabled ? -1 : 0}
            onClick={!isDisabled && onClick}
            onKeyDown={handleKeyDown}
            clickable={!isDisabled}
            role={role}
            aria-checked={ariaChecked}
            reducedHeight={!!subtitle}
            isDisabled={isDisabled}
        >
            {icon && (
                <OptionIcon>
                    <SVGInline svg={icon} />
                </OptionIcon>
            )}

            <OptionName data-test-id={`header-account-settingsoptions-${title}`}>
                {translate(title)}
                {subtitle && <OptionNameSub aria-hidden>{subtitle}</OptionNameSub>}
                {isExternalLink && <AriaHint>{translate('ARIA_HINT_NEW_TAB')}</AriaHint>}
                {ariaHint && <AriaHint>{ariaHint}</AriaHint>}
            </OptionName>

            <OptionRightSide>
                {actionComponent || (
                    <OptionButton isExternal={isExternalLink}>
                        <SVGInline svg={isExternalLink ? icons.externalLink : icons.rightArrowIcon} />
                    </OptionButton>
                )}
            </OptionRightSide>
        </Option>
    );
};

export default OptionFocusable;
